/**
 * NOTIFICATIONS — one global load, no polling.
 *
 * ── WHY NOT POLLING ───────────────────────────────────────────────────────
 *
 * The first version asked the server every thirty seconds. That is a request
 * per user per half-minute forever, and ours are not cheap: every call runs
 * JWT verification, then actingContext validating the tier and company headers
 * against resolve_actable_tiers() IN THE DATABASE, then the query.
 *
 * For an MVP that is spending a lot to learn "nothing happened". The bell is
 * read on page load and on demand instead — which is how people actually use
 * it: you refresh, you glance, you carry on.
 *
 * Live push belongs on the SSE stream that already exists for messages, as an
 * event type rather than a second transport. That is the next step, not a
 * different design.
 *
 * ── WHY A CONTEXT AND NOT A HOOK PER COMPONENT ────────────────────────────
 *
 * The bell shows a count and the notifications page shows the list. Both read
 * the same thing, and two independent hooks would fetch it twice and then
 * disagree the moment one of them refreshed. One load, one state, one truth.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from "react";
import {
  fetchNotifications, markNotificationsRead, type Notification,
} from "@/lib/api/notifications";

interface Ctx {
  notifications: Notification[];
  unread: number;
  /** Re-read. Call it after an act that probably produced one. */
  refresh: () => Promise<void>;
  /** Clear the badge. The rows stay — the page is a record, not a queue. */
  markRead: () => Promise<void>;
}

const NotificationsCtx = createContext<Ctx>({
  notifications: [],
  unread: 0,
  refresh: async () => {},
  markRead: async () => {},
});

export function NotificationsProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const { notifications: rows, unread: n } = await fetchNotifications();
      setNotifications(rows);
      setUnread(n);
    } catch {
      /* Signed out, or acting as nobody yet. A bell that cannot load must not
         break the shell it sits in. */
    }
  }, []);

  /* Once, on mount. A refresh of the page is a refresh of the bell, which is
     exactly the behaviour asked for. */
  useEffect(() => { void refresh(); }, [refresh]);

  /* And again when the tab comes back. Someone who left the app open, did
     something in another window and came back would otherwise see a stale
     count — the one case where "on load" alone reads as broken. */
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") void refresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const markRead = useCallback(async () => {
    if (unread === 0) return;
    try {
      await markNotificationsRead();
      setUnread(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch {
      /* Leave the badge up rather than lying about having cleared it. */
    }
  }, [unread]);

  const value = useMemo(
    () => ({ notifications, unread, refresh, markRead }),
    [notifications, unread, refresh, markRead],
  );

  return (
    <NotificationsCtx.Provider value={value}>{children}</NotificationsCtx.Provider>
  );
}

export function useNotifications(): Ctx {
  return useContext(NotificationsCtx);
}
